import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Phone, Lock, Zap, Shield, Clock, ArrowRight, Sparkles } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'

export default function LoginPage() {
  const navigate = useNavigate()
  const { login, isLoading, error, clearError, isAuthenticated, homePath } = useAuthStore()

  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [focusedField, setFocusedField] = useState<string | null>(null)

  useEffect(() => {
    if (isAuthenticated) {
      navigate(homePath(), { replace: true })
    }
  }, [isAuthenticated, navigate, homePath])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()

    try {
      // La normalisation du numéro (forme canonique « 226XXXXXXXX ») est
      // assurée par authApi.login — identique à la règle backend
      // (App\Support\Phone) : « +226… », « 00226… » ou « 70… » passent tous.
      await login(phone, password)
      navigate(homePath(), { replace: true })
    } catch {
      // Erreur gérée par le store
    }
  }

  return (
    <div className="flex min-h-screen font-['Inter',system-ui,sans-serif]">
      {/* ─── Left Panel - Branding ─────────────────────────────────── */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105"
          style={{ backgroundImage: "url('/bg-login.jpg')" }}
        />
        
        {/* Modern Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-600/90 via-amber-500/85 to-yellow-500/80" />
        
        {/* Animated Orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -left-20 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-float" />
          <div className="absolute top-1/3 -right-32 w-[500px] h-[500px] bg-yellow-300/20 rounded-full blur-3xl animate-float-delayed" />
          <div className="absolute -bottom-32 left-1/4 w-80 h-80 bg-orange-300/20 rounded-full blur-3xl animate-float-slow" />
        </div>

        {/* Mesh Gradient Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, white 1px, transparent 1px),
                              radial-gradient(circle at 75% 75%, white 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center w-full px-16">
          {/* Logo with Glow */}
          <div className="relative mb-10 group">
            <div className="absolute inset-0 bg-white/30 blur-2xl rounded-full scale-150 group-hover:scale-[1.7] transition-transform duration-700" />
            <div className="relative w-32 h-32 bg-white/95 backdrop-blur-xl rounded-[2rem] shadow-2xl flex items-center justify-center p-4 transform hover:scale-105 hover:rotate-3 transition-all duration-500">
              <img
                src="/logo.png"
                alt="GoFlash"
                className="w-full h-full object-contain drop-shadow-lg"
              />
            </div>
          </div>

          {/* Title with Modern Typography */}
          <div className="text-center mb-12">
            <h1 className="text-6xl font-black text-white mb-3 tracking-tight">
              Go<span className="text-yellow-200">Flash</span>
            </h1>
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="h-[2px] w-12 bg-white/40 rounded-full" />
              <Sparkles className="w-5 h-5 text-yellow-200" />
              <div className="h-[2px] w-12 bg-white/40 rounded-full" />
            </div>
            <p className="text-lg text-white/90 max-w-sm leading-relaxed">
              Centre de commande intelligent pour votre flotte VTC
            </p>
          </div>

          {/* Modern Feature Cards */}
          <div className="grid grid-cols-3 gap-4 w-full max-w-lg">
            <div className="group bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 hover:bg-white/20 hover:border-white/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-gradient-to-br from-white/30 to-white/10 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-white text-sm mb-1">Temps réel</h3>
              <p className="text-xs text-white/60 leading-relaxed">Suivi instantané</p>
            </div>
            <div className="group bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 hover:bg-white/20 hover:border-white/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-gradient-to-br from-white/30 to-white/10 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-white text-sm mb-1">Sécurisé</h3>
              <p className="text-xs text-white/60 leading-relaxed">Données chiffrées</p>
            </div>
            <div className="group bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 hover:bg-white/20 hover:border-white/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-gradient-to-br from-white/30 to-white/10 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-white text-sm mb-1">24/7</h3>
              <p className="text-xs text-white/60 leading-relaxed">Toujours actif</p>
            </div>
          </div>
        </div>

        {/* Modern Bottom Curve */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 180" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-auto">
            <path d="M0 180L48 165C96 150 192 120 288 105C384 90 480 90 576 97.5C672 105 768 120 864 127.5C960 135 1056 135 1152 127.5C1248 120 1344 105 1392 97.5L1440 90V180H1392C1344 180 1248 180 1152 180C1056 180 960 180 864 180C768 180 672 180 576 180C480 180 384 180 288 180C192 180 96 180 48 180H0Z" fill="white" fillOpacity="0.08"/>
            <path d="M0 180L48 172.5C96 165 192 150 288 142.5C384 135 480 135 576 140C672 145 768 155 864 157.5C960 160 1056 155 1152 150C1248 145 1344 140 1392 137.5L1440 135V180H1392C1344 180 1248 180 1152 180C1056 180 960 180 864 180C768 180 672 180 576 180C480 180 384 180 288 180C192 180 96 180 48 180H0Z" fill="white" fillOpacity="0.05"/>
          </svg>
        </div>
      </div>

      {/* ─── Right Panel - Login Form ──────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-16 bg-gradient-to-br from-slate-50 via-white to-orange-50/30 relative overflow-hidden">
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #f97316 1px, transparent 0)`,
          backgroundSize: '32px 32px'
        }} />
        
        {/* Floating decoration */}
        <div className="absolute top-20 right-20 w-72 h-72 bg-gradient-to-br from-orange-200/20 to-amber-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-64 h-64 bg-gradient-to-br from-yellow-200/15 to-orange-200/15 rounded-full blur-3xl" />

        <div className="w-full max-w-md relative z-10">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-10">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 blur-xl opacity-50 rounded-3xl" />
              <div className="relative w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-xl flex items-center justify-center p-3">
                <img src="/logo.png" alt="GoFlash" className="w-full h-full object-contain" />
              </div>
            </div>
          </div>

          {/* Modern Header */}
          <div className="text-center lg:text-left mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-100 to-amber-100 rounded-full mb-6">
              <div className="w-2 h-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full animate-pulse" />
              <span className="text-sm font-semibold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                Espace Administration
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-black text-slate-900 mb-3 tracking-tight">
              Bon retour<span className="text-orange-500">.</span>
            </h2>
            <p className="text-slate-500 text-lg">
              Accédez à votre tableau de bord sécurisé
            </p>
          </div>

          {/* Error Alert - Modern Style */}
          {error && (
            <div className="mb-8 flex items-center gap-4 rounded-2xl bg-gradient-to-r from-red-50 to-rose-50 border border-red-100 p-5 animate-shake shadow-lg shadow-red-100/50">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-rose-500 rounded-xl flex items-center justify-center shrink-0 shadow-lg shadow-red-200">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="font-bold text-red-800 text-sm">Erreur de connexion</p>
                <p className="text-red-600 text-sm mt-0.5">{error}</p>
              </div>
            </div>
          )}

          {/* Modern Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Phone Field */}
            <div className="group">
              <label htmlFor="phone" className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-3">
                <Phone className="w-4 h-4 text-orange-500" />
                Numéro de téléphone
              </label>
              <div className="relative">
                <div className={`absolute inset-0 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl blur-lg transition-opacity duration-300 ${focusedField === 'phone' ? 'opacity-20' : 'opacity-0'}`} />
                <div className={`relative flex items-center bg-white rounded-2xl border-2 transition-all duration-300 shadow-sm hover:shadow-md ${focusedField === 'phone' ? 'border-orange-500 shadow-lg shadow-orange-100' : 'border-slate-200'}`}>
                  <div className="flex items-center pl-5 pr-3 border-r border-slate-100">
                    <span className="text-lg">🇧🇫</span>
                    <span className="ml-2 text-slate-600 font-medium text-sm">+226</span>
                  </div>
                  <input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onFocus={() => setFocusedField('phone')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="70 00 00 00"
                    required
                    className="flex-1 h-16 px-4 bg-transparent text-slate-900 text-lg placeholder-slate-300 focus:outline-none font-medium"
                  />
                </div>
              </div>
            </div>

            {/* Password Field */}
            <div className="group">
              <label htmlFor="password" className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-3">
                <Lock className="w-4 h-4 text-orange-500" />
                Mot de passe
              </label>
              <div className="relative">
                <div className={`absolute inset-0 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl blur-lg transition-opacity duration-300 ${focusedField === 'password' ? 'opacity-20' : 'opacity-0'}`} />
                <div className={`relative flex items-center bg-white rounded-2xl border-2 transition-all duration-300 shadow-sm hover:shadow-md ${focusedField === 'password' ? 'border-orange-500 shadow-lg shadow-orange-100' : 'border-slate-200'}`}>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Entrez votre mot de passe"
                    required
                    className="flex-1 h-16 pl-5 pr-4 bg-transparent text-slate-900 text-lg placeholder-slate-300 focus:outline-none font-medium"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="flex items-center justify-center w-14 h-16 text-slate-400 hover:text-orange-500 transition-colors"
                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Submit Button - Premium Style */}
            <button
              type="submit"
              disabled={isLoading}
              className="relative w-full h-16 mt-8 rounded-2xl font-bold text-lg overflow-hidden group disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-500"
            >
              {/* Animated background gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 bg-[length:200%_100%] animate-gradient-x group-hover:shadow-xl group-hover:shadow-orange-200" />
              
              {/* Shine effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
              </div>
              
              {/* Content */}
              <span className={`relative z-10 flex items-center justify-center gap-3 text-white transition-all duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}>
                Se connecter
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
              
              {/* Loading spinner */}
              {isLoading && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                </span>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-10 text-center">
            <p className="text-sm text-slate-400">
              © 2026 <span className="font-semibold text-slate-500">GoFlash</span>. Tous droits réservés.
            </p>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(-5deg); }
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-15px) scale(1.05); }
        }
        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 10s ease-in-out infinite;
          animation-delay: 2s;
        }
        .animate-float-slow {
          animation: float-slow 12s ease-in-out infinite;
          animation-delay: 4s;
        }
        .animate-gradient-x {
          animation: gradient-x 3s ease infinite;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }
        .animate-shake {
          animation: shake 0.4s ease-in-out;
        }
      `}</style>
    </div>
  )
}
